/**
 * Authentication Context
 * 
 * Provides authentication state and methods throughout the application.
 * Integrates with Firebase Auth to track user authentication state.
 */

import React, { createContext, useContext, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { onAuthStateChange } from "./authService";
import { getUserData } from "./userService";
import { buildAppUser } from "./userModel";
import {
  setUser,
  clearUser,
  setInitializing,
  selectUser,
  selectAuthLoading,
  selectAuthError,
  selectIsAuthenticated,
  selectIsInitializing,
} from "./authSlice";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const loading = useSelector(selectAuthLoading);
  const error = useSelector(selectAuthError);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isInitializing = useSelector(selectIsInitializing);

  useEffect(() => {
    // Set initializing state to true when starting
    dispatch(setInitializing(true));
    
    let initializationComplete = false;
    let hasReceivedAuthState = false;
    
    const completeInitialization = () => {
      if (!initializationComplete) {
        initializationComplete = true;
        // Small delay to ensure state is set
        setTimeout(() => {
          dispatch(setInitializing(false));
        }, 100);
      }
    };
    
    // Subscribe to Firebase auth state changes
    // This will fire immediately with current auth state, then on any changes
    const unsubscribe = onAuthStateChange(async (firebaseUser) => {
      hasReceivedAuthState = true;
      
      try {
        if (firebaseUser) {
          // User is signed in - get additional data from Firebase Database
          try {
            const dbUserData = await getUserData(firebaseUser.uid);
            const tokenResult = await firebaseUser.getIdTokenResult().catch(() => null);
            const userData = buildAppUser({
              firebaseUser,
              dbUserData,
              tokenClaims: tokenResult?.claims || {},
            });
            dispatch(setUser(userData));
          } catch (error) {
            console.error("Error loading user data:", error);
            // Fallback to basic user data if database fetch fails
            const tokenResult = await firebaseUser.getIdTokenResult().catch(() => null);
            const userData = buildAppUser({
              firebaseUser,
              dbUserData: { role: "reader" },
              tokenClaims: tokenResult?.claims || {},
            });
            dispatch(setUser(userData));
          }
        } else {
          // User is signed out - clear state
          dispatch(clearUser());
        }
      } catch (error) {
        console.error("Error in auth state change:", error);
        // On error, clear user state
        dispatch(clearUser());
      } finally {
        // Complete initialization after first callback
        completeInitialization();
      }
    });

    // Fallback: ensure initialization completes even if callback is delayed
    // Give Firebase more time to restore session (up to 3 seconds)
    const timeoutId = setTimeout(() => {
      if (!hasReceivedAuthState) {
        // If we haven't received auth state yet, Firebase might be slow
        // Wait a bit more, but eventually complete initialization
        console.warn("Auth state change delayed, completing initialization");
        completeInitialization();
      } else {
        // We received auth state, just ensure initialization completes
        completeInitialization();
      }
    }, 3000);

    // Cleanup on unmount
    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, [dispatch]);

  const login = useCallback(
    (userData) => {
      dispatch(setUser(userData));
    },
    [dispatch]
  );

  const logout = useCallback(async () => {
    try {
      const { logoutThunk } = await import("./authThunks");
      await dispatch(logoutThunk()).unwrap();
    } catch (error) {
      console.error("Logout error:", error);
      dispatch(clearUser());
    }
  }, [dispatch]);

  const value = useMemo(
    () => ({
      isAuthenticated,
      user,
      loading: loading || isInitializing,
      error,
      isInitializing,
      login,
      logout,
    }),
    [
      isAuthenticated,
      user,
      loading,
      isInitializing,
      error,
      login,
      logout,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

